<template>
<div>
    <ac-modal :title="title" ref="modal">
        <template>
            <div v-if="widgetReady">
                <div v-if="endDateMessage" class="alert alert-warning">
                    {{endDateMessage}}
                    <a v-if="newFee && newFee.startDate" @click.prevent="showFutureFeeModal">
                        - View updated fee beginning {{newFee.startDate | shortDate}}
                    </a>
                </div>
                <fee-form :fee-form-instance="dataInstance" ref="feeForm">

                </fee-form>
                <div class="row" v-if="fee && fee.externalPayee">
                    <div class="col-md-12">
                        <h4>External Payee</h4>
                        <ac-button class="btn-sm btn-blue" :action="showPayeeModal">EDIT PAYEE</ac-button>
                        <span style="margin-left: 10px;">
                            <strong>Name:</strong> {{fee.externalPayee.name}}, 
                            <strong>Email:</strong> {{fee.externalPayee.email}}
                        </span>
                    </div>
                </div>
                <div class="row" v-if="fee && fee.externalPayer">
                    <div class="col-md-12">
                        <h4>External Payer</h4>
                        <ac-button class="btn-sm btn-blue" :action="showPayerModal">EDIT PAYER</ac-button>
                        <span style="margin-left: 10px;">
                            <strong>Name:</strong> {{fee.externalPayer.name}}, 
                            <strong>Email:</strong> {{fee.externalPayer.email}}
                        </span>
                    </div>
                </div>
            </div>
        </template>
        <template slot="buttons" v-if="userCanEdit && !endDateInPast">
            <template>
                <ac-button class="btn-sm btn-red" :action="cancel">CANCEL</ac-button>
                <ac-button class="btn-sm btn-green" :action="showConfirmModal">UPDATE FEE</ac-button>
            </template>
        </template>
    </ac-modal>
    <ac-confirmation-modal ref="confirmModal" :size-class="'modal-md'"
        :title="'Confirm Start Date'" :accept-button="'CONFIRM'" :cancel-button="'CANCEL'">
        <div v-if="!updateFeeImmediately" class="alert alert-warning">
            This fee update will begin on the first of next month.
        </div>
        <div>
            <input type="checkbox" class="form-check-input" id="cloneFee" v-model="updateFeeImmediately" />
            <label class="form-check-label" for="cloneFee">
                Fee update should take affect immediately
            </label>
        </div>
    </ac-confirmation-modal>
    <ac-modal :title="'View Future Fee'" ref="viewFutureFee">
        <template>
            <div v-if="widgetReady && futureDataInstance">
                <fee-form :fee-form-instance="futureDataInstance" ref="futureFeeForm">

                </fee-form>
            </div>
        </template>
    </ac-modal>
    <ac-modal :title="payeeTitle" ref="payeeEditor">
        <template>
            <div class="row" v-if="fee && fee.externalPayee">
                <div class="col-md-6">
                    <div class="form-group">
                        <label>Name</label>
                        <small><span class="redText">required</span></small>
                        <input type="text" class="form-control" placeholder="Name" v-model="fee.externalPayee.name">
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="form-group">
                        <label>Email</label>
                        <input type="text" class="form-control" placeholder="Email" v-model="fee.externalPayee.email">
                    </div>
                </div>
            </div>
            <div v-else>
                Missing External Payee
            </div>
        </template>
        <template slot="buttons">
            <template>
                <ac-button class="btn-sm btn-red" :action="closePayeeModal">CANCEL</ac-button>
                <ac-button class="btn-sm btn-green" :action="updatePayee" v-if="userCanEdit && !endDateInPast">UPDATE PAYEE</ac-button>
            </template>
        </template>
    </ac-modal>
    <ac-modal :title="payerTitle" ref="payerEditor">
        <template>
            <div class="row" v-if="fee && fee.externalPayer">
                <div class="col-md-6">
                    <div class="form-group">
                        <label>Name</label>
                        <small><span class="redText">required</span></small>
                        <input type="text" class="form-control" placeholder="Name" v-model="fee.externalPayer.name">
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="form-group">
                        <label>Email</label>
                        <input type="text" class="form-control" placeholder="Email" v-model="fee.externalPayer.email">
                    </div>
                </div>
            </div>
            <div v-else>
                Missing External Payer
            </div>
        </template>
        <template slot="buttons">
            <template>
                <ac-button class="btn-sm btn-red" :action="closePayerModal">CANCEL</ac-button>
                <ac-button class="btn-sm btn-green" :action="updatePayer" v-if="userCanEdit && !endDateInPast">UPDATE PAYER</ac-button>
            </template>
        </template>
    </ac-modal>
</div>
</template>

<script>
    import {FeeFormClass} from "./FeeFormClass.js";
    import FeeForm from "./FeeForm.vue";
    import {api} from "../../../global/framework/api.js";
    import {validators} from "../../../global/validators.js";
    import {notifications} from "../../../global/notifications.js";
	import {common} from "../../../global/common.js";
    import {mapGetters} from "vuex";
    import moment from 'moment';
    import { formatter } from '../../../global/formatter.js';

    export default {
        name: "UpdateFeeModal",
        components: {FeeForm},
        props: {allowedTypes: {type: [Array], required: true}},
        data() {
            return {
                widgetReady: false,
                dataInstance: null,
                futureDataInstance: null,
                fee: null,
                newFee: null,
                endDate: null,
                updateFeeImmediately: false,
            }
        },
        computed: {
            ...mapGetters(["userCanEdit"]),
            endDateInPast() {
                return moment(this.endDate).startOf('day').isBefore(moment().startOf('day'));
            },
            title() {
                return this.endDateInPast ? "View Fee" : "Update Fee"
            },
            payeeTitle() {
                return this.endDateInPast ? "View Payee" : "Update Payee"
            },
            payerTitle() {
                return this.endDateInPast ? "View Payer" : "Update Payer"
            },
            endDateMessage() {
                if (!this.endDate) return "";

                var formattedDate = formatter.formatDate(this.endDate, true);

                if (this.endDateInPast) return "This fee ended on: " + formattedDate;
                return "This fee will end on: " + formattedDate;
            }
        },
        methods: {
            async showModal(feeId) {
                let self = this;
                this.fee = await api.get('Fee', feeId, 'GetFeeWithParsedFormula');
                this.newFee = this.fee.childFee;
                this.endDate = this.fee.endDate;
                // console.log("UpdateFeeModal showModal fee:", this.fee);

                this.dataInstance = new class DS extends FeeFormClass {
                    async getAllowedTypes() {
                        return self.allowedTypes;
                    }

                    async getTemplate() {
                        return self.fee;
                    }
                };

                this.widgetReady = true;
                this.$refs.modal.open();
            },
            cancel() {
                this.$refs.modal.close();
            },
            async showFutureFeeModal() {
                var self = this;
                // console.log("UpdateFeeModal showFutureFeeModal newFee:", this.newFee);

                this.futureDataInstance = new class DS extends FeeFormClass {
                    async getAllowedTypes() { return self.allowedTypes; }
                    async getTemplate() { return self.newFee;  }
                };

                // this.cancel();
                this.$refs.viewFutureFee.open();
            },
            showPayeeModal() {
                this.cancel();
                this.$refs.payeeEditor.open();
            },
            closePayeeModal() {
                this.$refs.payeeEditor.close();
            },
            async updatePayee() {
                if (this.fee.externalPayee && this.fee.externalPayee.email 
                    && !validators.validateEmail(this.fee.externalPayee.email)) {
                    notifications.show({
                            title: 'Please enter a valid email address.',
                        },
                        common.ACRESSA_NOTIFICATIONS.ERROR);
                    return;
                }

                this.fee.externalPayee.accresaFee = 1;
                this.fee.externalPayee.dontValidateBanking = true;
                let result = await api.post('Fee', 'UpdatePayeeBasic', this.fee.externalPayee);
                if (result) {
                    this.$bus.$emit(this.$bus.$AcEvents.PAYEE_CHANGE, result);
                    this.closePayeeModal();
                }
            },

            showPayerModal() {
                this.cancel();
                this.$refs.payerEditor.open();
            },
            closePayerModal() {
                this.$refs.payerEditor.close();
            },
            async updatePayer() {
                if (this.fee.externalPayer && this.fee.externalPayer.email 
                    && !validators.validateEmail(this.fee.externalPayer.email)) {
                    notifications.show({
                            title: 'Please enter a valid email address.',
                        },
                        common.ACRESSA_NOTIFICATIONS.ERROR);
                    return;
                }

                this.fee.externalPayer.accresaFee = 1;
                this.fee.externalPayer.dontValidateBanking = true;
                let result = await api.post('Fee', 'UpdatePayeeBasic/true', this.fee.externalPayer);

                if (result) {
                    this.$bus.$emit(this.$bus.$AcEvents.PAYEE_CHANGE, result);
                    this.closePayerModal();
                }
            },


            async showConfirmModal() {
                let agree = await this.$refs.confirmModal.showModal();

                if (agree) {
                    this.updateFee();
                }
            },
            async updateFee() {
                let data = await this.$refs.feeForm.getFee();

                if (data) {
                    if (data.externalPayee) {
                        data.externalPayee = this.fee.externalPayee;
                    }

                    let result = this.updateFeeImmediately 
                        ? await api.postDataAsJson('Fee', 'UpdateFee', data)
                        : await api.postDataAsJson('Fee', 'CloneAndCreateFee', data);

                    if (result) {
                        this.$refs.modal.close();
                        this.$bus.$emit(this.$bus.$AcEvents.FEE_CHANGE, {});
                    }
                }
            }
        }
    }
</script>

<style scoped>

</style>